/** @format */

import React from "react";
import { Box, Button, CircularProgress, Collapse, Typography } from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ReactMarkdown from "react-markdown";
import ChartView from "./ChartView";
import DataTable from "./DataTable";
import Suggestions from "./Suggestions";
import ExportButton from "./ExportButton";
import FeedbackBar from "./FeedbackBar";
import BackingDataDownload from "./BackingDataDownload";

const cardSx = {
  maxWidth: { xs: "100%", md: "94%" },
  backgroundColor: "#fff",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  p: 2,
  boxShadow: "0 1px 2px 0 rgba(15,23,42,0.04)",
  animation: "propilot-slide-up 0.3s ease-out",
  "@keyframes propilot-slide-up": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const UserMessage = ({ content }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      px: { xs: 1.25, md: 2 },
      py: 0.5,
    }}
  >
    <Box
      sx={{
        maxWidth: { xs: "85%", md: "75%" },
        px: 2,
        py: 1.25,
        borderRadius: "16px",
        borderTopRightRadius: "4px",
        fontSize: 14,
        color: "#fff",
        backgroundColor: "#3b82f6",
        lineHeight: 1.55,
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      }}
    >
      {content}
    </Box>
  </Box>
);

const Header = ({ title, icon, color, accent }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mb: 1.5,
      pb: 1,
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        backgroundColor: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {React.cloneElement(icon, { sx: { color, fontSize: 14 } })}
    </Box>
    <Box sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{title}</Box>
  </Box>
);

const MarkdownText = ({ children, sx }) => (
  <Box
    sx={{
      fontSize: 14,
      color: "#1e293b",
      lineHeight: 1.65,
      "& p": { m: 0, mb: 1 },
      "& p:last-child": { mb: 0 },
      "& ul, & ol": { my: 1, pl: 3 },
      "& li": { mb: 0.5 },
      "& code": {
        fontSize: 13,
        backgroundColor: "#f1f5f9",
        px: 0.75,
        py: 0.25,
        borderRadius: "4px",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      },
      "& pre": {
        backgroundColor: "#1e293b",
        color: "#f1f5f9",
        p: 1.5,
        borderRadius: "8px",
        overflow: "auto",
        fontSize: 13,
        "& code": { backgroundColor: "transparent", color: "inherit", p: 0 },
      },
      "& a": { color: "#6366f1", textDecoration: "underline" },
      "& strong": { color: "#0f172a", fontWeight: 600 },
      ...sx,
    }}
  >
    <ReactMarkdown>{(children || "").toString()}</ReactMarkdown>
  </Box>
);

// Plain-English description of how the data was fetched (backend
// `text.query_explanation`). Collapsed by default — a subtle transparency
// disclosure so users can see exactly which slice was searched and how the
// metric was computed.
const QueryExplanation = ({ explanation }) => {
  const [open, setOpen] = React.useState(false);
  if (!explanation) return null;
  return (
    <Box sx={{ mt: 1.25 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
          color: "#64748b",
          userSelect: "none",
          "&:hover": { color: "#475569" },
        }}
      >
        <TravelExploreRoundedIcon sx={{ fontSize: 15 }} />
        How was this calculated?
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 16,
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 0.75,
            p: 1.25,
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            fontSize: 12.5,
            color: "#475569",
            lineHeight: 1.6,
          }}
        >
          {explanation}
        </Box>
      </Collapse>
    </Box>
  );
};

const ResponseBody = ({
  response,
  onSuggestion,
  onAnalyze,
  isAnalyzing,
  analysis,
  isFetching,
}) => {
  const text = response.text || {};
  const summary = text.summary;
  const insight = text.insight;
  const queryExplanation = text.query_explanation;
  const showAnalyzeButton = !!text.analyze_deeper && !analysis;

  return (
    <>
      {summary && <MarkdownText>{summary}</MarkdownText>}
      {insight && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mt: 1.25,
            fontSize: 13,
            fontStyle: "italic",
            color: "#64748b",
            lineHeight: 1.6,
          }}
        >
          <Box component="span" sx={{ fontStyle: "normal", flexShrink: 0 }}>
            💡
          </Box>
          <Box>{insight}</Box>
        </Box>
      )}
      <QueryExplanation explanation={queryExplanation} />
      {response.chart && <ChartView chart={response.chart} />}
      {response.table && <DataTable table={response.table} />}
      {showAnalyzeButton && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              isAnalyzing ? (
                <CircularProgress size={14} thickness={5} />
              ) : (
                <InsightsRoundedIcon />
              )
            }
            onClick={onAnalyze}
            disabled={isAnalyzing || isFetching}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              borderRadius: "8px",
              borderColor: "#c7d2fe",
              color: "#4338ca",
              backgroundColor: "#eef2ff",
              "&:hover": {
                borderColor: "#a5b4fc",
                backgroundColor: "#e0e7ff",
              },
            }}
          >
            {isAnalyzing ? "Analyzing…" : "Analyze deeper"}
          </Button>
        </Box>
      )}
      {analysis && analysis.type !== "error" && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            backgroundColor: "#eef2ff",
            border: "1px solid #c7d2fe",
            borderRadius: "10px",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#4338ca",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 0.75,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <InsightsRoundedIcon sx={{ fontSize: 14 }} /> Deeper Analysis
          </Typography>
          {analysis.text?.summary && (
            <MarkdownText sx={{ fontSize: 13 }}>
              {analysis.text.summary}
            </MarkdownText>
          )}
          {analysis.chart && <ChartView chart={analysis.chart} />}
        </Box>
      )}
      {analysis && analysis.type === "error" && (
        <Box
          sx={{
            mt: 1.5,
            p: 1,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            fontSize: 12,
            color: "#b91c1c",
          }}
        >
          {analysis.user_message || "Analysis failed."}
        </Box>
      )}
      <Suggestions
        items={response.suggestions}
        onClick={onSuggestion}
        disabled={isFetching}
      />
    </>
  );
};

const ClarificationCard = ({ response, onSuggestion, isFetching }) => {
  const options = response.options || [];
  // Default to allowing free-text — backend ships allow_free_text=true on every
  // clarification today; old payloads may omit the field.
  const freeTextEnabled = response.allow_free_text !== false;
  return (
    <Box sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: { xs: 1.25, md: 2 },
        py: 0.5,
      }}>
      <Box sx={cardSx}>
        <Header
          title="ProPilot"
          icon={<HelpOutlineRoundedIcon />}
          color="#2563eb"
          accent="#dbeafe"
        />
        {response.question && <MarkdownText>{response.question}</MarkdownText>}
        {options.length > 0 && (
          <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {options.map((opt, i) => {
              const text = typeof opt === "string" ? opt : opt?.text;
              const action = typeof opt === "string" ? null : opt?.action;
              return (
                <Button
                  key={i}
                  size="small"
                  variant="outlined"
                  disabled={isFetching}
                  onClick={() => onSuggestion({ text, action })}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 500,
                    borderRadius: "8px",
                    borderColor: "#bfdbfe",
                    color: "#1d4ed8",
                    backgroundColor: "#eff6ff",
                    "&:hover": {
                      borderColor: "#93c5fd",
                      backgroundColor: "#dbeafe",
                    },
                  }}
                >
                  {text}
                </Button>
              );
            })}
          </Box>
        )}
        {freeTextEnabled && (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: 12,
              color: "#64748b",
              fontStyle: "italic",
            }}
          >
            …or type your own answer in the box below.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ErrorCard = ({ response }) => (
  <Box sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: { xs: 1.25, md: 2 },
        py: 0.5,
      }}>
    <Box sx={{ ...cardSx, backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
      <Header
        title="ProPilot · Error"
        icon={<ErrorOutlineRoundedIcon />}
        color="#dc2626"
        accent="#fee2e2"
      />
      <Typography sx={{ fontSize: 14, color: "#7f1d1d", lineHeight: 1.6 }}>
        {response.user_message || "Something went wrong. Please try again."}
      </Typography>
    </Box>
  </Box>
);

const ExportCard = ({ response, onSuggestion, isFetching, feedbackProps }) => (
  <Box sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: { xs: 1.25, md: 2 },
        py: 0.5,
      }}>
    <Box sx={cardSx}>
      <Header
        title="ProPilot"
        icon={<BoltRoundedIcon />}
        color="#6366f1"
        accent="#eef2ff"
      />
      {response.text?.summary && (
        <MarkdownText>{response.text.summary}</MarkdownText>
      )}
      <ExportButton response={response} />
      <Suggestions
        items={response.suggestions}
        onClick={onSuggestion}
        disabled={isFetching}
      />
      {feedbackProps && <FeedbackBar {...feedbackProps} />}
    </Box>
  </Box>
);

const StandardCard = ({
  response,
  onSuggestion,
  onAnalyze,
  isAnalyzing,
  analysis,
  isFetching,
  feedbackProps,
  sessionId,
}) => {
  // Offer backing-data downloads only on answers that actually carry data
  // (a table or chart) — pure-text replies have nothing to "prove".
  const hasData = !!(response.table || response.chart);
  return (
    <Box sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: { xs: 1.25, md: 2 },
        py: 0.5,
      }}>
      <Box sx={cardSx}>
        <Header
          title="ProPilot"
          icon={<BoltRoundedIcon />}
          color="#6366f1"
          accent="#eef2ff"
        />
        <ResponseBody
          response={response}
          onSuggestion={onSuggestion}
          onAnalyze={onAnalyze}
          isAnalyzing={isAnalyzing}
          analysis={analysis}
          isFetching={isFetching}
        />
        {hasData && sessionId && <BackingDataDownload sessionId={sessionId} />}
        {feedbackProps && <FeedbackBar {...feedbackProps} />}
      </Box>
    </Box>
  );
};

const AssistantMessage = ({
  response,
  onSuggestion,
  onAnalyze,
  isAnalyzing,
  analysis,
  isFetching,
  feedbackProps,
  sessionId,
}) => {
  if (!response) return null;
  const type = response.type;

  if (type === "error" || response.ok === false) {
    return <ErrorCard response={response} />;
  }
  if (type === "clarification") {
    return (
      <ClarificationCard
        response={response}
        onSuggestion={onSuggestion}
        isFetching={isFetching}
      />
    );
  }
  if (type === "export") {
    return (
      <ExportCard
        response={response}
        onSuggestion={onSuggestion}
        isFetching={isFetching}
        feedbackProps={feedbackProps}
      />
    );
  }
  return (
    <StandardCard
      response={response}
      onSuggestion={onSuggestion}
      onAnalyze={onAnalyze}
      isAnalyzing={isAnalyzing}
      analysis={analysis}
      isFetching={isFetching}
      feedbackProps={feedbackProps}
      sessionId={sessionId}
    />
  );
};

const ChatMessage = ({
  turn,
  turnIndex,
  onSuggestion,
  onAnalyze,
  onFeedback,
  isAnalyzing,
  isFetching,
  sessionId,
}) => {
  // Feedback is only meaningful on a real answer — not on errors or
  // clarification prompts (those have nothing to rate yet).
  const botType = turn.bot?.type;
  const isRatable =
    turn.bot &&
    turn.bot.ok !== false &&
    botType !== "error" &&
    botType !== "clarification";

  const feedbackProps = isRatable
    ? {
        feedback: turn.feedback || null,
        hasError: !!turn.feedbackError,
        onVote: (rating, comment) => onFeedback(turnIndex, rating, comment),
      }
    : null;

  return (
    <>
      <UserMessage content={turn.user} />
      {turn.bot && (
        <AssistantMessage
          response={turn.bot}
          onSuggestion={(s) => onSuggestion(s, turnIndex)}
          onAnalyze={() => onAnalyze(turnIndex)}
          isAnalyzing={isAnalyzing}
          analysis={turn.analysis}
          isFetching={isFetching}
          feedbackProps={feedbackProps}
          sessionId={sessionId}
        />
      )}
    </>
  );
};

export default ChatMessage;
